/**
 * Volet de synthèse — Primitives partagées (médaillon, skeleton, tons, formats).
 *
 * Ces primitives garantissent le « au pixel » avec `SearchPreviewPanel` sans
 * copier-coller qui dérive : médaillon 48px identique, skeleton identique, styles
 * boutons repris. Le formatage (Money via devise, dates locales, pourcentages)
 * reste ici — responsabilité front (cohérente avec le reste de l'app).
 */

import type { ReactNode } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { initialsFromLabel } from '@/lib/searchCategories'
import { summaryIcon } from '@/lib/summaryIcons'
import { cn } from '@/lib/utils'
import type { SummaryHeader } from './types'

// ── Icône par clé string ─────────────────────────────────────────────────────

/** Rend une icône lucide depuis sa clé backend (décorative → `aria-hidden`). */
export function SummaryIcon({
    name,
    className,
}: {
    name: string | null | undefined
    className?: string
}): ReactNode {
    const Icon = summaryIcon(name)
    if (!Icon) return null
    return <Icon className={className} aria-hidden="true" />
}

// ── Médaillon 48px (personne = avatar/initiales · entité = icône type) ────────

export function Medallion({ header }: { header: SummaryHeader }) {
    const person = header.kind === 'person'
    if (header.avatar_url) {
        return (
            <Avatar className="h-12 w-12">
                <AvatarImage src={header.avatar_url} alt="" loading="eager" />
                <AvatarFallback
                    className={
                        person ? 'bg-primary/10 text-sm font-semibold text-primary' : 'bg-muted'
                    }
                >
                    {person ? (
                        initialsFromLabel(header.label)
                    ) : (
                        <SummaryIcon name={header.icon} className="h-5 w-5 text-muted-foreground" />
                    )}
                </AvatarFallback>
            </Avatar>
        )
    }
    if (person) {
        return (
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                {initialsFromLabel(header.label)}
            </span>
        )
    }
    return (
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted">
            <SummaryIcon name={header.icon} className="h-5 w-5 text-muted-foreground" />
        </span>
    )
}

// ── Skeleton (identique à `SearchPreviewPanel.PreviewSkeleton`) ──────────────

export function SummarySkeleton() {
    return (
        <div className="flex flex-col gap-5 p-5" aria-hidden="true">
            <div className="flex items-start gap-3.5">
                <div className="h-12 w-12 shrink-0 rounded-full bg-muted animate-pulse motion-reduce:animate-none" />
                <div className="flex-1 space-y-2 pt-1">
                    <div className="h-4 w-2/3 rounded bg-muted animate-pulse motion-reduce:animate-none" />
                    <div className="h-3 w-1/2 rounded bg-muted animate-pulse motion-reduce:animate-none" />
                </div>
            </div>
            <div className="space-y-2.5">
                {[0, 1, 2, 3].map((i) => (
                    <div
                        key={i}
                        className="h-3 w-full rounded bg-muted animate-pulse motion-reduce:animate-none"
                    />
                ))}
            </div>
        </div>
    )
}

// ── Styles boutons (repris de `SearchPreviewPanel`) ──────────────────────────

export const BTN_PRIMARY = cn(
    'inline-flex items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2',
    'text-xs font-medium text-primary-foreground hover:opacity-90',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
)

export const BTN_SECONDARY = cn(
    'inline-flex items-center justify-center gap-1.5 rounded-md border border-border px-3 py-2',
    'text-xs font-medium text-foreground hover:bg-accent',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
)
